import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Link from "next/link";
import { Container, Col, Card, Form, Row, Button, Spinner } from "reactstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import Image from "next/image";
import { useRouter } from "next/router";

import {
  validatePassword,
  displayPasswordNotification,
} from "@/utils/common-functions";
import classes from "@/styles/auth/sign-up.module.css";
import { routes } from "@/utils/routes";
import InputGroup from "../common/input-group";
import useAlertBar from "@/hooks/useAlertBar";
import { registerService } from "@/services/authService";
import VerifyCode from "./verify-code";
import { phoneRegExp } from "@/utils/constants";
import About from "../generic/about";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  verifyReferralCodeService,
  processReferralCodeService,
} from "@/services/referralService";
import { shipvistaLargeLogo } from "@/utils/images";
import BlogCarousel from "../generic/blog-carousel";

type Inputs = {
  username: string;
  companyName: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  referralCode: string;
  isAgree: boolean;
};

const validationSchema = yup.object({
  username: yup
    .string()
    .max(50, "Full Name cannot be more than 50 characters")
    .required("Full Name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  companyName: yup
    .string()
    .max(50, "Company Name cannot be more than 50 characters"),
  phone: yup
    .string()
    .transform(function (value, originalValue) {
      return this.isType(value) && value !== null
        ? value?.replace(/[^\d]/g, "")
        : value;
    })
    .matches(phoneRegExp, "Phone number is not valid")
    .required("Phone number is required"),
  password: yup
    .string()
    .required("Password is required")
    .test("isValidPassword", "Invalid Password", function (value = "") {
      const { path, createError } = this;
      if (!validatePassword(value, "")) {
        return createError({
          path,
          message: displayPasswordNotification(value, ""),
        });
      }
      return true;
    }),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), ""], "Passwords must match")
    .required("Password confirm is required"),
});

export default function SignUp() {
  const router = useRouter();

  const [verifyModal, setVerifyModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const { openAlertBar } = useAlertBar();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema) as any,
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const redirectToHome = () => {
    router.push(routes.home);
  };

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    setLoading(true);
    try {
      const requestBody = {
        user_name: formData.username,
        email: formData.email ? formData.email.toLowerCase() : formData.email,
        company_name: formData.companyName,
        phone: formData.phone,
        password: formData.confirmPassword,
        // isBusiness: isBusiness,
        referralCode: formData.referralCode,
      };

      const { data } = await registerService(requestBody);

      if (data.status) {
        setVerifyModal(true);
        openAlertBar(
          `${data.message} to ${formData.email.toLowerCase()}`,
          true
        );
      } else {
        openAlertBar(data.message, false);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while creating your account. Please try again ",
        false
      );
      console.log(error);
    }
  };

  const processReferralCode = async () => {
    try {
      const { data } = await verifyReferralCodeService(
        router.query["affiliateLink"] as string
      );

      if (data.status) {
        // add cookie from backend to track user as they register
        // and give rewards to referrer

        await processReferralCodeService(
          router.query["affiliateLink"] as string
        );

        setValue("referralCode", router.query["affiliateLink"] as string);
      } else {
        router.push(routes.login);
      }
    } catch (e) {
      openAlertBar("Error processing referral code. ", false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";

    if (router.isReady && router.query["affiliateLink"]) {
      processReferralCode();
    }

    return () => {
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  return (
    <>
      <Container className="my-5">
        <Card className={`border-0 ${classes.mainCard} shadow`}>
          <Row className={classes.row}>
            {useIsMobile() ? null : (
              <Col
                className={
                  classes.col1 + " d-flex align-items-center flex-wrap rounded"
                }
                xs={12}
                lg={7}
              >
                <div className="mx-auto">
                  <div className="">
                    <Image
                      src={shipvistaLargeLogo}
                      alt="Shipvista Logo"
                      className="img-fluid mx-auto d-block"
                      width={320}
                      height={100}
                    />
                  </div>
                  <div>
                    <BlogCarousel />
                  </div>
                </div>
              </Col>
            )}
            <Col className={classes.col2} xs={12} lg={5}>
              <div className="mx-auto p-3 pe-4">
                <Form
                  onSubmit={handleSubmit(onSubmit)}
                  className="needs-validation"
                >
                  <h3 className="text-center mb-3">
                    Create A ShipVista Account
                  </h3>
                  <InputGroup
                    required
                    label="Full Name"
                    name="username"
                    register={register}
                    inputSize="sm"
                    error={errors.username?.message}
                  />
                  <InputGroup
                    required
                    label="Email Address"
                    name="email"
                    register={register}
                    inputSize="sm"
                    error={errors.email?.message}
                  />
                  <InputGroup
                    label="Company Name"
                    name="companyName"
                    register={register}
                    inputSize="sm"
                    error={errors.companyName?.message}
                  />
                  <InputGroup
                    required
                    label="Phone"
                    name="phone"
                    register={register}
                    inputSize="sm"
                    error={errors.phone?.message}
                  />
                  <InputGroup
                    required
                    label="Password"
                    type="password"
                    name="password"
                    register={register}
                    inputSize="sm"
                    error={errors.password?.message}
                  />
                  <InputGroup
                    required
                    label="Confirm Password"
                    type="password"
                    name="confirmPassword"
                    register={register}
                    inputSize="sm"
                    error={errors.confirmPassword?.message}
                  />
                  <InputGroup
                    label="Referral Code"
                    name="referralCode"
                    register={register}
                    inputSize="sm"
                    error={errors.referralCode?.message}
                  />
                  <Row>
                    <Col>
                      <label htmlFor="isAgree">
                        <span>
                          By clicking create account, you agree to{" "}
                          <Link href={routes.terms}>
                            ShipVista&#39;s Terms & Conditions
                          </Link>{" "}
                          and{" "}
                          <Link href={routes.privacyPolicy}>
                            Privacy Policy
                          </Link>
                        </span>
                      </label>
                    </Col>
                  </Row>

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-100 mt-2 ${classes.submitBtn}`}
                  >
                    CREATE ACCOUNT
                    {loading && <Spinner size="sm" className="ms-3" />}
                  </Button>
                </Form>
                <div className="mt-2">
                  Already have an account{" "}
                  <Link href={routes.login}>Login?</Link>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </Container>
      <VerifyCode
        isOpen={verifyModal}
        toggle={() => setVerifyModal((v) => !v)}
        userEmail={watch("email")}
        isRegister
      />
      <div className={classes.overLay} onClick={redirectToHome}></div>
      <div className={classes.container} onClick={redirectToHome}>
        <About />
      </div>
    </>
  );
}
